import { useState, useEffect } from 'react'
import { useRecoilState, useSetRecoilState } from 'recoil'
import clsx from 'clsx'
import BigNumber from 'bignumber.js'
import IconColorCancel from '@/components/icons/IconColorCancel'
import IconColorConfirm from '@/components/icons/IconColorConfirm'
import { marketMapState } from '@/recoil/Market'
import { popupNotificationListState } from '@/recoil/Notification'
import { isValidNumber } from '@/utils/numberFormat'
import { getErrorMessage } from '@/utils/order'

import './EditInput.css'

export default function EditInput({
  type,
  symbol,
  value,
  isFocus,
  onFocus = () => {},
  onBlur = () => {},
  onEditOrder = () => {},
  calculateOrderValue = () => {}
}) {
  const setPopupNotification = useSetRecoilState(popupNotificationListState)
  const [marketMap] = useRecoilState(marketMapState)
  const [lockValue, setLockValue] = useState(false)
  const [inputValue, setInputValue] = useState(value)
  const [error, setError] = useState('')
  const marketInfo = marketMap[symbol]
  const decimalPlaces =
    type === 'order_price' ? marketInfo.priceDecimal : marketInfo.sizeDecimal
  const minimumNotionalValueErrorMessage = `Minimum order value is ${marketInfo.min_notional}`

  const onCancel = () => {
    setInputValue(value)
    onBlur()
  }

  const onConfirm = () => {
    if (inputValue === '' || inputValue === value) {
      onBlur()
      return setInputValue(value)
    }

    if (error) {
      return
    }

    setLockValue(true)
    onBlur(true)
    onEditOrder(type, inputValue).catch(e => {
      const errorCode = e.response?.data?.code
      const errorMessage = e.response?.data?.message
      errorMessage &&
        setPopupNotification({
          notification: {
            key: Date.now(),
            status: 'error',
            code: errorCode,
            message: errorMessage
          }
        })
      setLockValue(false)
      onBlur(false)
    })
  }

  const onValueUpdate = e => {
    const value = e.target.value
    if (!isValidNumber(value) || BigNumber(value || 0).dp() > decimalPlaces) {
      return
    }
    setInputValue(value)

    if (value === '') {
      return
    }

    if (type === 'order_price') {
      let error = getErrorMessage('price', value, {
        min: marketInfo.quote_min,
        max: marketInfo.quote_max,
        tick: marketInfo.quote_tick
      })

      if (
        !error &&
        BigNumber(calculateOrderValue(value)).lt(marketInfo.min_notional)
      ) {
        error = minimumNotionalValueErrorMessage
      }
      setError(error)
      return
    }

    if (type === 'order_quantity') {
      let error = getErrorMessage('size', value, {
        min: marketInfo.base_min,
        max: marketInfo.base_max,
        tick: marketInfo.base_tick
      })
      if (
        !error &&
        BigNumber(calculateOrderValue(value)).lt(marketInfo.min_notional)
      ) {
        error = minimumNotionalValueErrorMessage
      }
      setError(error)
      return
    }
  }

  useEffect(() => {
    !isFocus && setError('')
    if (!lockValue && !isFocus && value !== inputValue) {
      return setInputValue(value)
    }
    if (lockValue && BigNumber(value).eq(inputValue)) {
      return setLockValue(false)
    }
  }, [lockValue, isFocus, value, inputValue])

  return (
    <div className={clsx('edit-input-container', { focus: isFocus })}>
      <IconColorCancel
        className="edit-input-button cancel-edit-button"
        onClick={onCancel}
      />
      <input
        type="text"
        className="edit-input"
        value={inputValue}
        onFocus={onFocus}
        onChange={onValueUpdate}
      />
      <IconColorConfirm
        className="edit-input-button confirm-edit-button"
        onClick={onConfirm}
      />
      {error && <div className="error-message">{error}</div>}
    </div>
  )
}
